import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useWorkspace } from "../app/workspace";
import { Icon } from "../components/icons";
import { HealthBadge, HealthRing, Avatar } from "../components/ui";
import { usd, shortDate, relDate } from "../lib/format";
import { buildGraphFromWorkspace } from "../lib/graphFromWorkspace";
import {
  bandOf, contractsForOrg, instancesForContract, childOrgs, usersForInstance,
  rollupOrg, rollupContract, resolvePerson, orgById, personById,
  type GraphModel, type Rollup,
} from "../lib/graph";

type Sel = { type: "org" | "contract" | "instance"; id: string };

export default function Relationships() {
  const { ws } = useWorkspace();
  const g = useMemo(() => buildGraphFromWorkspace(ws), [ws]);
  const [params] = useSearchParams();

  // top-level orgs (no parent), biggest book first
  const roots = useMemo(
    () => g.orgs.filter((o) => !o.parentOrgId).sort((a, b) => rollupOrg(g, b.id).revenue - rollupOrg(g, a.id).revenue),
    [g],
  );

  // deep-link support: /relationships?account=a1 preselects that org
  const acctParam = params.get("account");
  const initialOrg = acctParam && g.orgs.some((o) => o.id === acctParam) ? acctParam : roots[0]?.id;
  const [sel, setSel] = useState<Sel>({ type: "org", id: initialOrg });
  const [personId, setPersonId] = useState<string>(g.people.find((p) => p.id === "p_jordan_ellis")?.id ?? g.people[0].id);
  useEffect(() => { if (acctParam && g.orgs.some((o) => o.id === acctParam)) setSel({ type: "org", id: acctParam }); }, [acctParam, g]);

  return (
    <div className="page">
      <div className="row between mb-24" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Relationships</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>Your book as a graph.</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>
            The same accounts you steward, seen as the graph they really are: each account owns a contract,
            every contract runs across {g.vocab.instancePlural.toLowerCase()} with their own users and usage, and a
            person is not the same thing as a login. Roll everything up to a parent company, or open any single
            {" "}{g.vocab.instance.toLowerCase()} — or the account itself — on its own.
          </p>
        </div>
      </div>

      <div className="detail-grid">
        {/* hierarchy tree */}
        <div className="card">
          <div className="card__hd">
            <div><h3>Customer hierarchy</h3><span className="sub">{g.vocab.org} → {g.vocab.contract} → {g.vocab.instance}</span></div>
          </div>
          <div className="card__bd">
            <div className="tree">
              {roots.map((r) => <OrgBranch key={r.id} g={g} orgId={r.id} depth={0} sel={sel} onSelect={setSel} />)}
            </div>
          </div>
        </div>

        {/* detail / rollup panel */}
        <DetailPanel g={g} sel={sel} onSelectPerson={(pid) => setPersonId(pid)} />
      </div>

      {/* People & identity resolution */}
      <div className="mt-24">
        <div className="section-title" style={{ marginBottom: 6 }}>People &amp; identity</div>
        <p className="muted" style={{ fontSize: 13.5, marginBottom: 16, maxWidth: 760 }}>
          One person, many logins. Penumbrai resolves every user back to a single person — and holds their
          affiliations to more than one organization at once, so "who is this a contact for?" gets an honest
          answer instead of a forced one.
        </p>
        <div className="detail-grid">
          <div className="card">
            <div className="card__hd"><h3>People</h3><span className="tag">{g.people.length}</span></div>
            <div className="card__bd" style={{ padding: 8 }}>
              {g.people.map((p) => {
                const emp = orgById(g, p.employerOrgId);
                return (
                  <div key={p.id} className={`person-pill${personId === p.id ? " sel" : ""}`} onClick={() => setPersonId(p.id)}>
                    <Avatar name={p.name} />
                    <div style={{ minWidth: 0 }}>
                      <b style={{ fontSize: 13.5 }}>{p.name}</b>
                      <div className="t-sub faint" style={{ fontSize: 12 }}>{p.title} · {emp?.name}</div>
                    </div>
                    <span className="tag" style={{ marginLeft: "auto" }}>{g.users.filter((u) => u.personId === p.id).length} users</span>
                  </div>
                );
              })}
            </div>
          </div>

          <PersonPanel g={g} personId={personId} />
        </div>
      </div>
    </div>
  );
}

/* --------------------------- hierarchy tree --------------------------- */

function OrgBranch({ g, orgId, depth, sel, onSelect }: { g: GraphModel; orgId: string; depth: number; sel: Sel; onSelect: (s: Sel) => void }) {
  const org = orgById(g, orgId)!;
  const kids = childOrgs(g, orgId);
  const contracts = contractsForOrg(g, orgId);
  const rl = rollupOrg(g, orgId);

  return (
    <>
      <div className={`tree-row${sel.type === "org" && sel.id === orgId ? " sel" : ""}`} onClick={() => onSelect({ type: "org", id: orgId })}>
        <Icon name="building" size={14} className="caret" />
        <span className="t-ic" style={{ background: org.color }}>{org.name[0]}</span>
        <div>
          <div className="t-name">{org.name}</div>
          <div className="t-sub">{org.type}</div>
        </div>
        <div className="t-right">
          <span className="num muted" style={{ fontSize: 12.5 }}>{usd(rl.revenue)}</span>
          <HealthBadge band={bandOf(rl.health)} label={`${rl.health}`} />
        </div>
      </div>

      <div className="tree-guide">
        {kids.map((k) => <OrgBranch key={k.id} g={g} orgId={k.id} depth={depth + 1} sel={sel} onSelect={onSelect} />)}
        {contracts.map((c) => {
          const instances = instancesForContract(g, c.id);
          return (
            <div key={c.id}>
              <div className={`tree-row${sel.type === "contract" && sel.id === c.id ? " sel" : ""}`} onClick={() => onSelect({ type: "contract", id: c.id })}>
                <Icon name="target" size={13} className="caret" />
                <span className="t-ic" style={{ background: "var(--ink-2)" }}><Icon name="target" size={13} /></span>
                <div>
                  <div className="t-name">{c.name}</div>
                  <div className="t-sub">{c.revenueType === "services" ? "Services" : "Subscription"} · {usd(c.amount)} {c.amountLabel}</div>
                </div>
                <div className="t-right"><span className="tree-kind">{c.status}</span></div>
              </div>
              <div className="tree-guide">
                {instances.map((i) => (
                  <div key={i.id} className={`tree-row${sel.type === "instance" && sel.id === i.id ? " sel" : ""}`} onClick={() => onSelect({ type: "instance", id: i.id })}>
                    <Icon name={"layers"} size={13} className="caret" />
                    <span className="t-ic" style={{ background: "var(--surface)", color: "var(--ink)", border: "1px solid var(--line)" }}>
                      <Icon name={"layers"} size={12} />
                    </span>
                    <div>
                      <div className="t-name">{i.name}</div>
                      <div className="t-sub">{usersForInstance(g, i.id).length} users · {i.metrics[0].label} {i.metrics[0].value}</div>
                    </div>
                    <div className="t-right"><HealthBadge band={bandOf(i.health)} label={`${i.health}`} /></div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* --------------------------- detail / rollup --------------------------- */

function RollupGrid({ rl }: { rl: Rollup }) {
  return (
    <div className="rollup-grid">
      <div className="rollup-cell"><div className="rl">{rl.revenueLabel}</div><div className="rv">{usd(rl.revenue)}</div></div>
      <div className="rollup-cell"><div className="rl">Avg health</div><div className="rv" style={{ color: `var(--${rl.health >= 70 ? "good" : rl.health >= 45 ? "watch" : "risk"})` }}>{rl.health}</div></div>
      <div className="rollup-cell"><div className="rl">People</div><div className="rv">{rl.people}</div></div>
      <div className="rollup-cell"><div className="rl">Contracts</div><div className="rv">{rl.contracts}</div></div>
      <div className="rollup-cell"><div className="rl">Instances</div><div className="rv">{rl.instances}</div></div>
      <div className="rollup-cell"><div className="rl">Active users</div><div className="rv">{rl.activeUsers}</div></div>
    </div>
  );
}

function DetailPanel({ g, sel, onSelectPerson }: { g: GraphModel; sel: Sel; onSelectPerson: (id: string) => void }) {
  if (sel.type === "org") {
    const org = orgById(g, sel.id)!;
    const rl = rollupOrg(g, sel.id);
    const parent = org.parentOrgId ? orgById(g, org.parentOrgId) : undefined;
    return (
      <div className="card">
        <div className="card__hd">
          <div className="row" style={{ gap: 12 }}>
            <span className="mono mono--lg" style={{ background: org.color, color: "#fff", border: "none" }}>{org.name[0]}</span>
            <div>
              <h3 style={{ fontSize: 18, fontFamily: "var(--serif)" }}>{org.name}</h3>
              <span className="sub">{org.type}{parent ? ` · part of ${parent.name}` : ""} · rolled up across everything below</span>
            </div>
          </div>
          <div className="row" style={{ gap: 8 }}>
            <Link to={`/relationships/${sel.id}`} className="btn btn--primary btn--sm">Relationship 360 <Icon name="arrowRight" size={13} /></Link>
            {org.accountId && <Link to={`/accounts/${org.accountId}`} className="btn btn--ghost btn--sm">Open account</Link>}
          </div>
        </div>
        <div className="card__bd" style={{ display: "grid", gap: 16 }}>
          <RollupGrid rl={rl} />
          <p className="muted" style={{ fontSize: 12.5 }}>
            This is the aggregated view. Every metric sums or averages all {g.vocab.instancePlural.toLowerCase()} beneath
            this organization{childOrgs(g, sel.id).length ? " and its child organizations" : ""}. Select a contract or
            {" "}{g.vocab.instance.toLowerCase()} in the tree to see it on its own.
          </p>
        </div>
      </div>
    );
  }

  if (sel.type === "contract") {
    const c = g.contracts.find((x) => x.id === sel.id)!;
    const rl = rollupContract(g, sel.id);
    const org = orgById(g, c.orgId);
    return (
      <div className="card">
        <div className="card__hd">
          <div><h3 style={{ fontSize: 17, fontFamily: "var(--serif)" }}>{c.name}</h3><span className="sub">{org?.name} · {c.revenueType === "services" ? "Services engagement" : "Subscription"}</span></div>
          <span className="tree-kind">{c.status}</span>
        </div>
        <div className="card__bd" style={{ display: "grid", gap: 16 }}>
          <div className="kv">
            <dt>{c.amountLabel}</dt><dd>{usd(c.amount)}</dd>
            <dt>Type</dt><dd>{c.revenueType === "services" ? "Services / project-based" : "Subscription / ARR"}</dd>
            <dt>Term</dt><dd>{shortDate(c.startDate)} → {shortDate(c.endDate)}</dd>
            <dt>{g.vocab.instancePlural}</dt><dd>{rl.instances}</dd>
          </div>
          <RollupGrid rl={rl} />
        </div>
      </div>
    );
  }

  // instance — independent view
  const i = g.instances.find((x) => x.id === sel.id)!;
  const users = usersForInstance(g, sel.id);
  const contract = g.contracts.find((c) => c.id === i.contractId);
  const org = contract ? orgById(g, contract.orgId) : undefined;
  return (
    <div className="card">
      <div className="card__hd">
        <div className="row" style={{ gap: 12 }}>
          <HealthRing value={i.health} size={44} />
          <div>
            <h3 style={{ fontSize: 17, fontFamily: "var(--serif)" }}>{i.name}</h3>
            <span className="sub">{g.vocab.instance} · {org?.name} · {contract?.name}</span>
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <Link to={`/projects/${i.id}`} className="btn btn--primary btn--sm">Open project 360 <Icon name="arrowRight" size={13} /></Link>
          {i.accountId && <Link to={`/accounts/${i.accountId}`} className="btn btn--ghost btn--sm">Account</Link>}
        </div>
      </div>
      <div className="card__bd" style={{ display: "grid", gap: 16 }}>
        <div className="rollup-grid">
          {i.metrics.map((m) => (
            <div className="rollup-cell" key={m.label}><div className="rl">{m.label}</div><div className="rv">{m.value}</div></div>
          ))}
        </div>
        <div>
          <div className="ff-mono faint" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8 }}>Users on this {g.vocab.instance.toLowerCase()}</div>
          {users.map((u) => {
            const p = personById(g, u.personId);
            return (
              <div className="affil" key={u.id} onClick={() => p && onSelectPerson(p.id)} style={{ cursor: "pointer" }}>
                <Avatar name={p?.name ?? "?"} size="sm" />
                <div className="grow">
                  <b style={{ fontSize: 13 }}>{p?.name}</b>
                  <span className="faint" style={{ fontSize: 12 }}> · {u.role}</span>
                </div>
                <span className={`badge ${u.active ? "badge--good" : "badge--neutral"}`}>{u.active ? `active ${relDate(u.lastActive)}` : "inactive"}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* --------------------------- person resolution --------------------------- */

function PersonPanel({ g, personId }: { g: GraphModel; personId: string }) {
  const res = useMemo(() => resolvePerson(g, personId), [g, personId]);
  if (!res) return <div className="card"><div className="empty">Select a person</div></div>;
  const { person, employer, users, appearsUnder, affiliations, instanceCount, activeUserCount } = res;

  return (
    <div className="card">
      <div className="card__hd">
        <div className="row" style={{ gap: 12 }}>
          <Avatar name={person.name} size="lg" />
          <div>
            <h3 style={{ fontSize: 18, fontFamily: "var(--serif)" }}>{person.name}</h3>
            <span className="sub">{person.title} · {employer?.name} · {person.email}</span>
          </div>
        </div>
      </div>
      <div className="card__bd" style={{ display: "grid", gap: 18 }}>
        <div className="rollup-grid" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
          <div className="rollup-cell"><div className="rl">Logins (users)</div><div className="rv">{users.length}</div></div>
          <div className="rollup-cell"><div className="rl">{g.vocab.instancePlural}</div><div className="rv">{instanceCount}</div></div>
          <div className="rollup-cell"><div className="rl">Active</div><div className="rv">{activeUserCount}</div></div>
        </div>

        {/* the "who is this a contact for" answer */}
        <div>
          <div className="ff-mono faint" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8 }}>Appears under</div>
          <div className="appears">
            {appearsUnder.map(({ org, via }) => (
              <div className="appears-card" key={org.id}>
                <span className="t-ic" style={{ background: org.color, width: 30, height: 30 }}>{org.name[0]}</span>
                <div className="grow">
                  <b style={{ fontSize: 13.5 }}>{org.name}</b>
                  <div className="faint" style={{ fontSize: 12 }}>{via}</div>
                </div>
                <span className="tree-kind">{org.type}</span>
              </div>
            ))}
          </div>
          {appearsUnder.length > 1 && (
            <p className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
              {person.name.split(" ")[0]} is reachable from more than one side — a single forced "account" would lose that.
            </p>
          )}
        </div>

        {/* identity resolution: all logins */}
        <div>
          <div className="ff-mono faint" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8 }}>All logins, resolved to one person</div>
          {users.map((u) => (
            <div className="affil" key={u.id}>
              <span className="t-ic" style={{ background: "var(--surface-2)", color: "var(--ink)", border: "1px solid var(--line)", width: 26, height: 26 }}>
                <Icon name={"layers"} size={12} />
              </span>
              <div className="grow">
                <b style={{ fontSize: 13 }}>{u.instance?.name}</b>
                <span className="faint" style={{ fontSize: 12 }}> · {u.role}{u.org ? ` · ${u.org.name}` : ""}</span>
              </div>
              <span className={`badge ${u.active ? "badge--good" : "badge--neutral"}`}>{u.active ? relDate(u.lastActive) : "inactive"}</span>
            </div>
          ))}
        </div>

        {/* explicit affiliations */}
        <div>
          <div className="ff-mono faint" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8 }}>Affiliations</div>
          {affiliations.map((a) => (
            <div className="affil" key={a.id}>
              <span className="chip chip--active">{a.role}</span>
              <div className="grow">
                <b style={{ fontSize: 13 }}>{a.targetName}</b>
                <span className="faint" style={{ fontSize: 12 }}> · {a.targetKind}</span>
                {a.note && <div className="faint" style={{ fontSize: 12 }}>{a.note}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
